// Fathom's notes, drawn — T70 (14 Sep 2026). The reader is src/markdown.ts; this only draws its
// blocks, and a link opens in a new tab as every outside link here does.
import { parseMarkdown, type Inline } from '../markdown.ts';

function Inlines({ inline }: { inline: Inline[] }) {
  return (
    <>
      {inline.map((run, i) =>
        run.kind === 'bold' ? (
          <strong key={i}>{run.text}</strong>
        ) : run.kind === 'link' ? (
          <a key={i} href={run.href} target="_blank" rel="noreferrer">
            {run.text}
          </a>
        ) : run.kind === 'break' ? (
          <br key={i} />
        ) : (
          <span key={i}>{run.text}</span>
        ),
      )}
    </>
  );
}

/** The mark that says an answer is still being written (T81). */
const Caret = () => <i className="answer__caret" aria-hidden="true" />;

/**
 * `keepLines` (T176): each line of a paragraph stays a line, as kadmon writes her answers in the
 * brain's chat; Fathom's notes, wrapped at random, are joined.
 *
 * `caret` (T202 K-47): the writing caret, at the end of the last line of the last block, so it
 * stands beside the words and never on a line of its own under them.
 */
export function Markdown({
  text,
  keepLines = false,
  caret = false,
}: {
  text: string;
  keepLines?: boolean;
  caret?: boolean;
}) {
  const blocks = parseMarkdown(text, keepLines);
  const last = blocks.length - 1;
  // Words that are only space so far still show she is writing.
  if (caret && blocks.length === 0) {
    return (
      <div className="md">
        <p className="md__p">
          <Caret />
        </p>
      </div>
    );
  }
  return (
    <div className="md">
      {blocks.map((block, i) => {
        const end = caret && i === last ? <Caret /> : null;
        return block.kind === 'heading' ? (
          <h3 key={i} className={`md__h md__h--${block.level}`}>
            <Inlines inline={block.inline} />
            {end}
          </h3>
        ) : block.kind === 'list' ? (
          block.ordered ? (
            <ol key={i} className="md__list">
              {block.items.map((item, j) => (
                <li key={j}>
                  <Inlines inline={item} />
                  {j === block.items.length - 1 ? end : null}
                </li>
              ))}
            </ol>
          ) : (
            <ul key={i} className="md__list">
              {block.items.map((item, j) => (
                <li key={j}>
                  <Inlines inline={item} />
                  {j === block.items.length - 1 ? end : null}
                </li>
              ))}
            </ul>
          )
        ) : (
          <p key={i} className="md__p">
            <Inlines inline={block.inline} />
            {end}
          </p>
        );
      })}
    </div>
  );
}
